<script setup>
import { ref, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import ResizableEditorWrapper from 'dashboard/components/widgets/conversation/ResizableEditorWrapper.vue';
import InternalChatAudioRecorder from './InternalChatAudioRecorder.vue';
import InternalChatEditor from './InternalChatEditor.vue';
import InternalChatComposerActions from './InternalChatComposerActions.vue';
import { useInternalChatComposer } from './useInternalChatComposer';

const props = defineProps({
  containerHeight: { type: Number, default: 0 },
  disabled: { type: Boolean, default: false },
  error: { type: String, default: '' },
  draft: { type: Object, default: null },
  members: { type: Object, default: () => ({ users: [], teams: [] }) },
  canMentionAll: { type: Boolean, default: false },
  isThread: { type: Boolean, default: false },
});

const emit = defineEmits(['send', 'draftChange', 'typing']);
const { t } = useI18n();
const audioRecorder = ref(null);
const {
  content,
  contentFormat,
  isContentEmpty,
  attachments,
  fileInput,
  textInput,
  audioVisible,
  audioBusy,
  audioError,
  startAudio,
  discardAudio,
  finishAudio,
  failAudio,
  resizableEditor,
  editorExpanded,
  editorSizeLabel,
  toggleEditorSize,
  mentionMenuOpen,
  alsoSendInChannel,
  canSend,
  validationError,
  openFilePicker,
  onFilesSelected,
  removeAttachment,
  toggleMentionMenu,
  submit,
  resetIfUnchanged,
  onInput,
} = useInternalChatComposer(props, emit, toRef(props, 'draft'));
defineExpose({ reset: resetIfUnchanged, retry: submit });
</script>

<template>
  <ResizableEditorWrapper
    ref="resizableEditor"
    class="shrink-0 p-2"
    :data-testid="
      isThread ? 'internal-chat-thread-editor' : 'internal-chat-channel-editor'
    "
    :container-height="containerHeight"
    editor-selector=".ProseMirror"
    :reset-on-message-sent="false"
  >
    <form
      class="rounded-xl border border-n-weak bg-n-solid-1"
      @submit.prevent="submit"
    >
      <div class="flex items-start justify-between gap-2 px-3 pt-2">
        <div v-if="attachments.length" class="flex min-w-0 flex-wrap gap-2">
          <span
            v-for="file in attachments"
            :key="`${file.name}-${file.lastModified}`"
            class="flex max-w-full items-center gap-2 rounded-md bg-n-slate-3 px-2 py-1 text-xs text-n-slate-12"
          >
            <span class="max-w-48 truncate">{{ file.name }}</span>
            <Button
              type="button"
              variant="ghost"
              color="slate"
              size="xs"
              icon="i-lucide-x"
              :disabled="disabled"
              :aria-label="
                t('INTERNAL_CHAT.REMOVE_ATTACHMENT', { name: file.name })
              "
              @click="removeAttachment(file)"
            />
          </span>
        </div>
        <Button
          type="button"
          variant="ghost"
          color="slate"
          size="sm"
          class="ms-auto shrink-0"
          :icon="editorExpanded ? 'i-lucide-minimize-2' : 'i-lucide-maximize-2'"
          :aria-label="editorSizeLabel"
          :title="editorSizeLabel"
          :aria-expanded="editorExpanded"
          data-testid="internal-chat-expand-editor"
          @click="toggleEditorSize"
        />
      </div>
      <div class="relative px-3 py-2">
        <input
          ref="fileInput"
          type="file"
          class="hidden"
          multiple
          :disabled="disabled"
          :aria-label="t('INTERNAL_CHAT.ATTACH_FILES')"
          @change="onFilesSelected"
        />
        <InternalChatEditor
          ref="textInput"
          v-model:content-format="contentFormat"
          :model-value="content"
          :class="{ invisible: audioVisible }"
          class="[&_.ProseMirror]:min-h-[clamp(var(--editor-min-allowed),var(--editor-height),var(--editor-max-allowed))] [&_.ProseMirror]:max-h-[clamp(var(--editor-min-allowed),var(--editor-height),var(--editor-max-allowed))] [&_.ProseMirror]:overflow-y-auto [&_.ProseMirror]:!mb-0 [&_.ProseMirror]:[transition:min-height_var(--editor-height-transition),max-height_var(--editor-height-transition)]"
          :placeholder="t('INTERNAL_CHAT.MESSAGE_PLACEHOLDER')"
          :disabled="disabled || audioVisible"
          :label="t('INTERNAL_CHAT.MESSAGE_LABEL')"
          :can-submit="canSend && !audioBusy"
          :members="members"
          :can-mention-all="canMentionAll"
          @update:model-value="onInput"
          @empty-change="isContentEmpty = $event"
          @typing="emit('typing', $event)"
          @submit="submit"
        />
        <InternalChatAudioRecorder
          v-if="audioVisible"
          ref="audioRecorder"
          @ready="finishAudio"
          @busy-change="audioBusy = $event"
          @error="failAudio"
        />
      </div>
      <InternalChatComposerActions
        v-model:also-send-in-channel="alsoSendInChannel"
        :disabled="disabled"
        :audio-visible="audioVisible"
        :audio-busy="audioBusy"
        :audio-recording="audioRecorder?.state"
        :can-send="canSend"
        :can-mention="
          Boolean(
            members.users?.length || members.teams?.length || canMentionAll
          )
        "
        :mention-menu-open="mentionMenuOpen"
        :is-thread="isThread"
        :audio-error="audioError"
        :error="error"
        :validation-error="validationError"
        @attach="openFilePicker"
        @record="startAudio()"
        @mention="toggleMentionMenu"
        @discard-audio="discardAudio"
        @audio-play-stop="audioRecorder.togglePlayStop()"
        @send="submit"
      />
    </form>
  </ResizableEditorWrapper>
</template>
